import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  ControlsContainer,
  HeaderButton,
} from '../common/styles';
import { MainContainer } from './styles';
import ConfirmationModal from '../common/ConfirmationModal';
// ADR-014: snapshot close ("close with unfinished work") confirmation — lists open work and
// requires a reason before the irreversible close.
import CloseSessionWithIncompleteModal from './components/CloseSessionWithIncompleteModal';
// ADR-193: delete a session (any status, with or without individuals) — shows
// what will be deleted and requires a reason.
import DeleteSessionModal from './components/DeleteSessionModal';
import { FEEDBACK_SESSION_CLOSURE_TYPES } from '../../constants/feedbackSessionStatuses';
import ResultsCount from '../common/ResultsCount';
import SessionGrid from './components/SessionGrid';
import SessionFilters from './components/SessionFilters';
import useFeedbackSessionsStore from '../../store/feedbackSessionsStore';
import useGridNavigation from '../../hooks/useGridNavigation';
import { useAlert } from '../../contexts/AlertContext';
import { useAbility } from '../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';
import { feedbackApi } from '../../services/api';
import { FEEDBACK_SESSION_VIEW_MODES } from '../../constants/feedbackSessionViewModes';
import { canDeleteSession, DELETION_PREVIEW_STALE_CODE } from '../../utils/feedbackSessionDeletion';

const FeedbackSessions = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const ability = useAbility();
  const gridRef = useRef(null);

  // Zustand store
  const {
    isLoading,
    isLoadingMore,
    pageInfo,
    searchTerm,
    selectedStatuses,
    selectedReviewTypes,
    showControls,
    fetchSessionsWithReset,
    refreshSessionsSilently,
    fetchSessionsLoadMore,
    setSearchTerm,
    toggleStatus,
    toggleReviewType,
    selectedYear,
    selectedBusinessUnitId,
    setSelectedYear,
    setSelectedBusinessUnitId,
    sessions,
    setShowControls,
    setIsReturningFromDetails,
    isReturningFromDetails,
    getFilteredSessions,
    removeSession,
    updateSession,
    resetFilters,
  } = useFeedbackSessionsStore();

  // Modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [configureModalOpen, setConfigureModalOpen] = useState(false);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  // ADR-014: snapshot close ("close with unfinished work") modal
  const [closeEarlyModalOpen, setCloseEarlyModalOpen] = useState(false);
  const [sessionToPublish, setSessionToPublish] = useState(null);

  // Grid navigation hook
  const {
    handleItemClick,
    handleGridScroll,
    showLoadingSpinner,
    shouldHideContent,
    loadMoreData,
    shouldLoadData,
  } = useGridNavigation({
    gridRef,
    items: getFilteredSessions(),
    columnCount: 3,
    rowHeight: 580,
    routes: {
      list: '/feedback-sessions',
      detail: () => `/feedback-sessions/view`,
      new: '/feedback-sessions/new',
      additionalRoutes: [
        { path: '/view', shouldLoadData: false },
        { path: '/edit', shouldLoadData: false },
        { path: '/edit-employees', shouldLoadData: false },
        { path: '/edit-reviewers', shouldLoadData: false },
        { path: '/report', shouldLoadData: false },
      ]
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    onNavigateAway: resetFilters,
    loadMoreDataCallback: fetchSessionsLoadMore,
    isLoading,
    isLoadingMore,
    pageInfo,
  });

  // Fetch sessions on mount
  useEffect(() => {
    if (shouldLoadData() && !isReturningFromDetails) {
      fetchSessionsWithReset();
    }
    if (isReturningFromDetails) {
      setIsReturningFromDetails(false);
      // ADR-192 §11: the detail pages may have changed this list (e.g. every
      // assignee removed → the session becomes deletable). Refresh in the
      // background so the cards are current without losing the scroll position.
      refreshSessionsSilently();
    }
  }, [shouldLoadData, isReturningFromDetails, fetchSessionsWithReset, setIsReturningFromDetails, refreshSessionsSilently]);

  // Helper to capture scroll state before navigation
  const captureScrollState = useCallback((session) => {
    const gridElement = gridRef.current?._outerRef;
    if (!gridElement) {
      return null;
    }

    const currentScrollPosition = gridElement.scrollTop;
    const items = getFilteredSessions();
    const itemIndex = items.findIndex(item => item.id === session.id);
    const rowIndex = itemIndex !== -1 ? Math.floor(itemIndex / 3) : -1;
    const itemTopPosition = rowIndex * 580;
    const offsetFromTop = itemTopPosition - currentScrollPosition;

    return {
      scrollPosition: currentScrollPosition,
      itemId: session.id,
      itemIndex,
      rowIndex,
      offsetFromTop,
      timestamp: Date.now()
    };
  }, [getFilteredSessions]);

  // Session action handlers
  const handleSession = useCallback(async (session, viewMode, route) => {
    try {
      const scrollState = captureScrollState(session);
      const sessionData = await feedbackApi.getSessionById(session.id);
      navigate(route, {
        state: {
          sessionData,
          viewMode,
          fromList: true,
          scrollState
        }
      });
    } catch (error) {
      showAlert(error.message || 'Failed to fetch session details', 'error', error);
    }
  }, [navigate, showAlert, captureScrollState]);

  const handleEditSession = useCallback((session) => {
    if (!ability.can(ACTIONS.MANAGE, SUBJECTS.FEEDBACK_SESSION_INSTANCE)) {
      showAlert('You do not have permission to edit feedback sessions', 'error');
      return;
    }
    handleSession(session, FEEDBACK_SESSION_VIEW_MODES.EDIT, '/feedback-sessions/edit');
  }, [ability, handleSession, showAlert]);

  const handleEditEmployees = useCallback((session) => {
    if (!ability.can(ACTIONS.UPDATE, SUBJECTS.FEEDBACK_SESSION_INSTANCE)) {
      showAlert('You do not have permission to edit feedback session employees', 'error');
      return;
    }
    handleSession(session, FEEDBACK_SESSION_VIEW_MODES.EDIT_EMPLOYEES, '/feedback-sessions/edit-employees');
  }, [ability, handleSession, showAlert]);

  const handleEditReviewers = useCallback((session) => {
    if (!ability.can(ACTIONS.UPDATE, SUBJECTS.FEEDBACK_SESSION_INSTANCE)) {
      showAlert('You do not have permission to edit feedback session reviewers', 'error');
      return;
    }
    handleSession(session, FEEDBACK_SESSION_VIEW_MODES.EDIT_REVIEWERS, '/feedback-sessions/edit-reviewers');
  }, [ability, handleSession, showAlert]);

  const handleViewSession = useCallback((session) => {
    handleSession(session, FEEDBACK_SESSION_VIEW_MODES.VIEW, '/feedback-sessions/view');
  }, [handleSession]);

  const handleViewReport = useCallback((session) => {
    if (!ability.can(ACTIONS.READ, SUBJECTS.FEEDBACK_SESSION_INSTANCE)) {
      showAlert('You do not have permission to view feedback session reports', 'error');
      return;
    }
    const scrollState = captureScrollState(session);
    navigate(`/feedback-sessions/report/${session.id}`, {
      state: {
        fromList: true,
        scrollState
      }
    });
  }, [ability, navigate, showAlert, captureScrollState]);

  // ADR-192: `canDelete` comes from the API (zero assignments + cluster-scoped
  // delete permission), so it is the only gate here; the API re-checks both.
  const handleDeleteClick = useCallback((session) => {
    if (!canDeleteSession(session)) {
      showAlert('This feedback session cannot be deleted', 'error');
      return;
    }
    setSessionToDelete(session);
    setDeleteModalOpen(true);
  }, [showAlert]);

  // ADR-193: reason + the counts the dialog showed (confirmedCounts). A stale
  // preview (409 DELETION_PREVIEW_STALE) is rethrown so the dialog can reload
  // the numbers and ask again; every other outcome closes the dialog.
  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSessionToDelete(null);
  }, []);

  const handleDeleteSession = useCallback(async (reason, confirmedCounts) => {
    if (!sessionToDelete) {
      return;
    }

    try {
      await feedbackApi.deleteSession(sessionToDelete.id, reason, confirmedCounts);
      removeSession(sessionToDelete.id);
      showAlert('Feedback session deleted successfully', 'success');
      closeDeleteModal();
    } catch (error) {
      if (error.status === 409 && error.data?.code === DELETION_PREVIEW_STALE_CODE) {
        throw error;
      }
      if (error.status === 404) {
        removeSession(sessionToDelete.id);
      } else if (error.status === 403) {
        updateSession({ id: sessionToDelete.id, canDelete: false });
      }
      showAlert(error.message || 'Failed to delete feedback session', 'error', error);
      closeDeleteModal();
    }
  }, [sessionToDelete, removeSession, updateSession, showAlert, closeDeleteModal]);

  const handlePublishToConfigurationClick = useCallback((session) => {
    if (!ability.can(ACTIONS.PUBLISH, SUBJECTS.FEEDBACK_SESSION_INSTANCE)) {
      showAlert('You do not have permission to publish feedback sessions', 'error');
      return;
    }
    setSessionToPublish(session);
    setConfigureModalOpen(true);
  }, [ability, showAlert]);

  const handlePublishToInProgressClick = useCallback((session) => {
    if (!ability.can(ACTIONS.PUBLISH, SUBJECTS.FEEDBACK_SESSION_INSTANCE)) {
      showAlert('You do not have permission to publish feedback sessions', 'error');
      return;
    }
    setSessionToPublish(session);
    setStartModalOpen(true);
  }, [ability, showAlert]);

  const handlePublishToCompletedClick = useCallback((session) => {
    if (!ability.can(ACTIONS.PUBLISH, SUBJECTS.FEEDBACK_SESSION_INSTANCE)) {
      showAlert('You do not have permission to publish feedback sessions', 'error');
      return;
    }
    setSessionToPublish(session);
    setCompleteModalOpen(true);
  }, [ability, showAlert]);

  // ADR-014: snapshot close ("close with unfinished work") — gated by the dedicated
  // FeedbackSessionClosure permission (§9), not the ordinary publish ability.
  const handleCloseWithIncompleteClick = useCallback((session) => {
    if (!ability.can(ACTIONS.MANAGE, SUBJECTS.FEEDBACK_SESSION_CLOSURE)) {
      showAlert('You do not have permission to close a session with unfinished work', 'error');
      return;
    }
    setSessionToPublish(session);
    setCloseEarlyModalOpen(true);
  }, [ability, showAlert]);

  const handleCloseWithIncomplete = useCallback(async (reason) => {
    if (!sessionToPublish) {
      return;
    }

    if (!ability.can(ACTIONS.MANAGE, SUBJECTS.FEEDBACK_SESSION_CLOSURE)) {
      showAlert('You do not have permission to close a session with unfinished work', 'error');
      setCloseEarlyModalOpen(false);
      setSessionToPublish(null);
      return;
    }

    try {
      await feedbackApi.closeWithIncomplete(sessionToPublish.id, reason);
      updateSession({
        ...sessionToPublish,
        completedAt: new Date(),
        closureType: FEEDBACK_SESSION_CLOSURE_TYPES.CLOSED_WITH_INCOMPLETE,
        closureReason: reason
      });
      showAlert('Feedback session closed — remaining work preserved as-is', 'success');
    } catch (error) {
      showAlert(error.message || 'Failed to close feedback session', 'error', error);
    } finally {
      setCloseEarlyModalOpen(false);
      setSessionToPublish(null);
    }
  }, [sessionToPublish, ability, updateSession, showAlert]);

  const handlePublishToConfiguration = useCallback(async () => {
    if (!sessionToPublish) {
      return;
    }

    if (!ability.can(ACTIONS.PUBLISH, SUBJECTS.FEEDBACK_SESSION_INSTANCE)) {
      showAlert('You do not have permission to publish feedback sessions', 'error');
      setConfigureModalOpen(false);
      setSessionToPublish(null);
      return;
    }

    try {
      await feedbackApi.publishToConfiguration(sessionToPublish.id);
      updateSession({ ...sessionToPublish, publishedAt: new Date() });
      showAlert('Feedback session published to configuration state successfully', 'success');
    } catch (error) {
      showAlert(error.message || 'Failed to publish feedback session', 'error', error);
    } finally {
      setConfigureModalOpen(false);
      setSessionToPublish(null);
    }
  }, [sessionToPublish, ability, updateSession, showAlert]);

  const handlePublishToInProgress = useCallback(async () => {
    if (!sessionToPublish) {
      return;
    }

    if (!ability.can(ACTIONS.PUBLISH, SUBJECTS.FEEDBACK_SESSION_INSTANCE)) {
      showAlert('You do not have permission to publish feedback sessions', 'error');
      setStartModalOpen(false);
      setSessionToPublish(null);
      return;
    }

    try {
      await feedbackApi.publishToInProgress(sessionToPublish.id);
      updateSession({ ...sessionToPublish, startedAt: new Date() });
      showAlert('Feedback session published to in progress state successfully', 'success');
    } catch (error) {
      showAlert(error.message || 'Failed to publish feedback session', 'error', error);
    } finally {
      setStartModalOpen(false);
      setSessionToPublish(null);
    }
  }, [sessionToPublish, ability, updateSession, showAlert]);

  const handlePublishToCompleted = useCallback(async () => {
    if (!sessionToPublish) {
      return;
    }

    if (!ability.can(ACTIONS.PUBLISH, SUBJECTS.FEEDBACK_SESSION_INSTANCE)) {
      showAlert('You do not have permission to publish feedback sessions', 'error');
      setCompleteModalOpen(false);
      setSessionToPublish(null);
      return;
    }

    try {
      await feedbackApi.publishToCompleted(sessionToPublish.id);
      updateSession({ ...sessionToPublish, completedAt: new Date() });
      showAlert('Feedback session published to completed state successfully', 'success');
    } catch (error) {
      showAlert(error.message || 'Failed to publish feedback session', 'error', error);
    } finally {
      setCompleteModalOpen(false);
      setSessionToPublish(null);
    }
  }, [sessionToPublish, ability, updateSession, showAlert]);

  const handleNewSession = useCallback(() => {
    navigate('/feedback-sessions/new');
  }, [navigate]);

  const filteredSessions = getFilteredSessions();

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Feedback Sessions</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton
            onClick={() => setShowControls(!showControls)}
            sx={{
              color: '#475569',
              borderColor: '#E2E8F0',
              '&:hover': {
                borderColor: '#CBD5E1',
                backgroundColor: '#F8FAFC',
              }
            }}
          >
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls}>
          <SessionFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedStatuses={selectedStatuses}
            onStatusToggle={toggleStatus}
            selectedReviewTypes={selectedReviewTypes}
            onReviewTypeToggle={toggleReviewType}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            selectedBusinessUnitId={selectedBusinessUnitId}
            onBusinessUnitChange={setSelectedBusinessUnitId}
            sessions={sessions}
            onNewSession={handleNewSession}
          />
        </ControlsContainer>

        <MainContainer>
          <ResultsCount count={filteredSessions.length} singular="session" plural="sessions" />

          <SessionGrid
            sessions={filteredSessions}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            pageInfo={{ ...pageInfo, totalCount: filteredSessions.length }}
            handleItemClick={handleItemClick}
            handleGridScroll={handleGridScroll}
            showLoadingSpinner={showLoadingSpinner}
            shouldHideContent={shouldHideContent}
            loadMoreData={loadMoreData}
            gridRef={gridRef}
            onView={handleViewSession}
            onEdit={handleEditSession}
            onEditEmployees={handleEditEmployees}
            onEditReviewers={handleEditReviewers}
            onDelete={handleDeleteClick}
            onPublishConfigure={handlePublishToConfigurationClick}
            onPublishStart={handlePublishToInProgressClick}
            onPublishComplete={handlePublishToCompletedClick}
            onCloseWithIncomplete={handleCloseWithIncompleteClick}
            onViewReport={handleViewReport}
            ability={ability}
            searchInputValue={searchTerm}
          />
        </MainContainer>
      </Content>

      <DeleteSessionModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteSession}
        session={sessionToDelete}
      />

      <ConfirmationModal
        isOpen={configureModalOpen}
        onClose={() => {
          setConfigureModalOpen(false);
          setSessionToPublish(null);
        }}
        onConfirm={handlePublishToConfiguration}
        title="Publish to Configuration"
        message={`Are you sure you want to publish the feedback session "${sessionToPublish?.name}" to configuration state? This will make the session available for configuration.`}
        actionButtonText="Publish"
        actionButtonColor="#CA8A04"
      />

      <ConfirmationModal
        isOpen={startModalOpen}
        onClose={() => {
          setStartModalOpen(false);
          setSessionToPublish(null);
        }}
        onConfirm={handlePublishToInProgress}
        title="Start Feedback Session"
        message={`Are you sure you want to start the feedback session "${sessionToPublish?.name}"? This will make the session available to all assigned individuals and reviewers.`}
        actionButtonText="Start"
        actionButtonColor="#0284C7"
      />

      <ConfirmationModal
        isOpen={completeModalOpen}
        onClose={() => {
          setCompleteModalOpen(false);
          setSessionToPublish(null);
        }}
        onConfirm={handlePublishToCompleted}
        title="Complete Feedback Session"
        message={`Are you sure you want to complete the feedback session "${sessionToPublish?.name}"? This will finalize the session and make it read-only.`}
        actionButtonText="Complete"
        actionButtonColor="#16A34A"
      />

      <CloseSessionWithIncompleteModal
        isOpen={closeEarlyModalOpen}
        onClose={() => {
          setCloseEarlyModalOpen(false);
          setSessionToPublish(null);
        }}
        onConfirm={handleCloseWithIncomplete}
        session={sessionToPublish}
      />
    </Container>
  );
};

export default FeedbackSessions;
